import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { handleThunkError } from '../common/util';
import { IClockingRequestQueueItem, ITechCommentRequestQueueItem, IVehicleAttachmentRequestQueueItem, RequestQueueGroup, RequestQueueType } from '../models/dms/requestQueue.model';
import { RootState } from '.';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { IAttendanceClocking, ILabourClocking } from '../models/dms/clocking.model';
import { addQueueToSelectedClockingHistoryStoreAsync, updateQueueToSelectedClockingHistoryStoreAsync } from './clockingHistorySlice';
import { logConsole, logTransaction } from '../common/log';
import { ITechComment } from '../models/dms/technicianComments.model';
import { addQueueToSelectedTechnicianCommentsStoreAsync, updateQueueToSelectedTechnicianCommentStoreAsync } from './technicianCommentSlice';
import { IAttachmentContainer } from '../models/dms/attachment.model';
import { LogType } from '../models/log.model';

interface IRequestQueueState {
  clockingQueue?: IClockingRequestQueueItem[];
  techCommentQueue?: ITechCommentRequestQueueItem[];
  vehicleAttachmentQueue?: IVehicleAttachmentRequestQueueItem[];
}

const initialState: IRequestQueueState = {
  clockingQueue: [],
  techCommentQueue: [],
  vehicleAttachmentQueue: [],
};

/// THUNKS

export const addToRequestQueueStoreAsync = createAsyncThunk<{}, IClockingRequestQueueItem | ITechCommentRequestQueueItem | IVehicleAttachmentRequestQueueItem>(
  'requestQueue/addToRequestQueueStoreAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, '', '[requestQueue] addToRequestQueueStoreAsync');
    try {
      const state = getState() as RootState;
      switch (payload.group) {
        case RequestQueueGroup.Clocking:
          logTransaction(LogType.Info, `Adding clocking item '${payload.id}' to request queue storage`);
          const clockingQueueState = [...(state.requestQueue.clockingQueue || [])];
          clockingQueueState.push(payload as IClockingRequestQueueItem);
          dispatch(setClockingRequestQueueState(clockingQueueState));
          await updateConfigObject<IClockingRequestQueueItem[]>(ConfigName.ClockingRequestQueue, clockingQueueState, false);
          await dispatch(addQueueToSelectedClockingHistoryStoreAsync({ data: (payload as IClockingRequestQueueItem).data, id: payload.id }));
          break;
        case RequestQueueGroup.TechComment:
          logTransaction(LogType.Info, `Adding tech comment item '${payload.id}' to request queue storage`);
          const techCommentQueueState = [...(state.requestQueue.techCommentQueue || [])];
          techCommentQueueState.push(payload as ITechCommentRequestQueueItem);
          dispatch(setTechCommentRequestQueueState(techCommentQueueState));
          await updateConfigObject<ITechCommentRequestQueueItem[]>(ConfigName.TechCommentRequestQueue, techCommentQueueState, false);
          await dispatch(addQueueToSelectedTechnicianCommentsStoreAsync({ data: (payload as ITechCommentRequestQueueItem).data, id: payload.id }));
          break;
        case RequestQueueGroup.VehicleAttachment:
          logTransaction(LogType.Info, `Adding vehicle attachment item '${payload.id}' to request queue storage`);
          const vehicleAttachmentQueueState = [...(state.requestQueue.vehicleAttachmentQueue || [])];
          vehicleAttachmentQueueState.push(payload as IVehicleAttachmentRequestQueueItem);
          dispatch(setVehicleAttachmentRequestQueueState(vehicleAttachmentQueueState));
          await updateConfigObject<IVehicleAttachmentRequestQueueItem[]>(ConfigName.VehicleAttachmentRequestQueue, vehicleAttachmentQueueState, false);
          break;
      }
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const removeFromRequestQueueStoreAsync = createAsyncThunk<{}, IClockingRequestQueueItem | ITechCommentRequestQueueItem | IVehicleAttachmentRequestQueueItem>(
  'requestQueue/removeFromRequestQueueStoreAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, '', '[requestQueue] removeFromRequestQueueStoreAsync');
    try {
      switch (payload.group) {
        case RequestQueueGroup.Clocking:
          logTransaction(LogType.Info, `Removing clocking item '${payload.id}' from request queue storage`);
          let clockingQueueState = [...((getState() as RootState).requestQueue.clockingQueue || [])];
          clockingQueueState = clockingQueueState.filter((queue) => {
            return queue.id !== payload.id;
          });
          dispatch(setClockingRequestQueueState(clockingQueueState));
          await updateConfigObject<IClockingRequestQueueItem[]>(ConfigName.ClockingRequestQueue, clockingQueueState, false);
          await dispatch(updateQueueToSelectedClockingHistoryStoreAsync(payload.id));
          break;
        case RequestQueueGroup.TechComment:
          logTransaction(LogType.Info, `Removing tech comment item '${payload.id}' from request queue storage`);
          let techCommentQueueState = [...((getState() as RootState).requestQueue.techCommentQueue || [])];
          techCommentQueueState = techCommentQueueState.filter((queue) => {
            return queue.id !== payload.id;
          });
          dispatch(setTechCommentRequestQueueState(techCommentQueueState));
          await updateConfigObject<ITechCommentRequestQueueItem[]>(ConfigName.TechCommentRequestQueue, techCommentQueueState, false);
          await dispatch(updateQueueToSelectedTechnicianCommentStoreAsync(payload.id));
          break;
        case RequestQueueGroup.VehicleAttachment:
          logTransaction(LogType.Info, `Removing vehicle attachment item '${payload.id}' from request queue storage`);
          let vehicleAttachmentQueueState = [...((getState() as RootState).requestQueue.vehicleAttachmentQueue || [])];
          vehicleAttachmentQueueState = vehicleAttachmentQueueState.filter((queue) => {
            return queue.id !== payload.id;
          });
          dispatch(setVehicleAttachmentRequestQueueState(vehicleAttachmentQueueState));
          await updateConfigObject<IVehicleAttachmentRequestQueueItem[]>(ConfigName.VehicleAttachmentRequestQueue, vehicleAttachmentQueueState, false);
          break;
      }
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const updateRequestQueueStoreAsync = createAsyncThunk<{}, IClockingRequestQueueItem | ITechCommentRequestQueueItem | IVehicleAttachmentRequestQueueItem>(
  'requestQueue/updateRequestQueueStoreAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, '', '[requestQueue] updateRequestQueueStoreAsync');
    try {
      switch (payload.group) {
        case RequestQueueGroup.Clocking:
          logTransaction(LogType.Info, `Updating clocking item '${payload.id}' in request queue storage`);
          let clockingQueueState = [...((getState() as RootState).requestQueue.clockingQueue || [])];
          const clockingQueueItemIndex = clockingQueueState.findIndex((queue) => {
            return queue.id === payload.id;
          });
          const clockingQueueItem = clockingQueueState[clockingQueueItemIndex];

          switch (clockingQueueItem.type) {
            case RequestQueueType.TechAttendanceOff:
            case RequestQueueType.TechAttendanceOn:
              const attendanceClocking = {
                ...(clockingQueueItem.data as IAttendanceClocking),
                ...(payload.data as IAttendanceClocking),
              } as IAttendanceClocking;
              clockingQueueState[clockingQueueItemIndex] = {
                ...payload,
                data: { ...attendanceClocking },
              };
              dispatch(setClockingRequestQueueState(clockingQueueState));
              await updateConfigObject<IClockingRequestQueueItem[]>(ConfigName.ClockingRequestQueue, clockingQueueState, false);
              break;
            case RequestQueueType.TechLabourOff:
            case RequestQueueType.TechLabourOn:
              const labourClocking = {
                ...(clockingQueueItem.data as ILabourClocking),
                ...(payload.data as ILabourClocking),
              } as ILabourClocking;
              clockingQueueState[clockingQueueItemIndex] = {
                ...payload,
                data: { ...labourClocking },
              };
              dispatch(setClockingRequestQueueState(clockingQueueState));
              await updateConfigObject<IClockingRequestQueueItem[]>(ConfigName.ClockingRequestQueue, clockingQueueState, false);
              break;
          }
          break;
        case RequestQueueGroup.TechComment:
          logTransaction(LogType.Info, `Updating tech comment item '${payload.id}' in request queue storage`);
          let techCommentQueueState = [...((getState() as RootState).requestQueue.techCommentQueue || [])];
          const techCommentQueueItemIndex = techCommentQueueState.findIndex((queue) => {
            return queue.id === payload.id;
          });
          const techCommentQueueItem = techCommentQueueState[techCommentQueueItemIndex];

          switch (techCommentQueueItem.type) {
            case RequestQueueType.TechComment:
              const techComment = {
                ...(techCommentQueueItem.data as ITechComment),
                ...(payload.data as ITechComment),
              } as ITechComment;
              techCommentQueueState[techCommentQueueItemIndex] = {
                ...payload,
                data: { ...techComment },
              };
              dispatch(setTechCommentRequestQueueState(techCommentQueueState));
              await updateConfigObject<ITechCommentRequestQueueItem[]>(ConfigName.TechCommentRequestQueue, techCommentQueueState, false);
              break;
          }
          break;
        case RequestQueueGroup.VehicleAttachment:
          logTransaction(LogType.Info, `Updating vehicle attachment item '${payload.id}' in request queue storage`);
          let vehicleAttachmentQueueState = [...((getState() as RootState).requestQueue.vehicleAttachmentQueue || [])];
          const vehicleAttachmentQueueItemIndex = vehicleAttachmentQueueState.findIndex((queue) => {
            return queue.id === payload.id;
          });
          const vehicleAttachmentQueueItem = vehicleAttachmentQueueState[vehicleAttachmentQueueItemIndex];

          switch (vehicleAttachmentQueueItem.type) {
            case RequestQueueType.VehicleAttachment:
              const vehicleAttachment = {
                ...(vehicleAttachmentQueueItem.data as IAttachmentContainer),
                ...(payload.data as IAttachmentContainer),
              } as IAttachmentContainer;
              vehicleAttachmentQueueState[vehicleAttachmentQueueItemIndex] = {
                ...payload,
                data: { ...vehicleAttachment },
              };
              dispatch(setVehicleAttachmentRequestQueueState(vehicleAttachmentQueueState));
              await updateConfigObject<IVehicleAttachmentRequestQueueItem[]>(ConfigName.VehicleAttachmentRequestQueue, vehicleAttachmentQueueState, false);
              break;
          }
          break;
      }
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const clearRequestQueueStoreAsync = createAsyncThunk<{}, void>('requestQueue/clearRequestQueueStoreAsync', async (nil, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Clearing request queue storage`, '[requestQueue] clearRequestQueueStoreAsync');
  try {
    logTransaction(LogType.Info, `Clearing clocking request queue`);
    dispatch(setClockingRequestQueueState([]));
    logTransaction(LogType.Info, `Clearing tech comment request queue`);
    dispatch(setTechCommentRequestQueueState([]));
    logTransaction(LogType.Info, `Clearing vehicle attachment request queue`);
    dispatch(setVehicleAttachmentRequestQueueState([]));
    await updateConfigObject<IClockingRequestQueueItem[]>(ConfigName.ClockingRequestQueue, [], false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

/// SLICE

export const requestQueueSlice = createSlice({
  name: 'requestQueue',
  initialState,
  reducers: {
    setClockingRequestQueueState: (state, action: PayloadAction<IClockingRequestQueueItem[]>) => {
      logConsole(LogType.Info, '[requestQueue] setRequestQueueState');
      state.clockingQueue = action.payload;
    },
    setTechCommentRequestQueueState: (state, action: PayloadAction<ITechCommentRequestQueueItem[]>) => {
      logConsole(LogType.Info, '[requestQueue] setTechCommentRequestQueueState');
      state.techCommentQueue = action.payload;
    },
    setVehicleAttachmentRequestQueueState: (state, action: PayloadAction<IVehicleAttachmentRequestQueueItem[]>) => {
      logConsole(LogType.Info, '[requestQueue] setVehicleAttachmentRequestQueueState');
      state.vehicleAttachmentQueue = action.payload;
    },
  },
});

/// REDUCERS

export const { setClockingRequestQueueState, setTechCommentRequestQueueState, setVehicleAttachmentRequestQueueState } = requestQueueSlice.actions;

export default requestQueueSlice.reducer;
